import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Pencil } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", phone: "", email: "", tags: "" };

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/contacts", { params: { search } });
      setContacts(data);
    } catch (e) {}
  };

  useEffect(() => { load(); }, [search]);

  const save = async () => {
    const payload = { name: form.name, phone: form.phone, email: form.email || null, tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (editing) await api.put(`/contacts/${editing}`, payload);
      else await api.post("/contacts", payload);
      toast.success(editing ? "Contact updated" : "Contact added");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact deleted");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const header = lines[0].toLowerCase().split(",").map((h) => h.trim());
    const nameIdx = header.findIndex((h) => h.includes("name"));
    const phoneIdx = header.findIndex((h) => h.includes("phone") || h.includes("number") || h.includes("mobile"));
    const tagsIdx = header.findIndex((h) => h.includes("tag"));
    if (phoneIdx === -1) { toast.error("CSV must have a 'phone' column"); return; }
    const rows = lines.slice(1).map((l) => {
      const cols = l.split(",");
      return { name: nameIdx >= 0 ? cols[nameIdx]?.trim() : "", phone: cols[phoneIdx]?.trim() || "", tags: tagsIdx >= 0 && cols[tagsIdx] ? cols[tagsIdx].split(";").map((t) => t.trim()).filter(Boolean) : [] };
    }).filter((r) => r.phone);
    try {
      const { data } = await api.post("/contacts/import", { contacts: rows });
      toast.success(`Imported ${data.imported} contacts (${data.skipped} skipped)`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Contacts & Tags</h1>
          <div className="font-hand text-lg text-amber-800">your whatsapp audience ✦</div>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="csv-file-input" onChange={(e) => importCsv(e.target.files?.[0])} />
          <button data-testid="import-csv-btn" className="btn-yellow" onClick={() => fileRef.current?.click()}><Upload size={15} /> Import CSV</button>
          <button data-testid="add-contact-btn" className="btn-navy" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}><Plus size={15} /> Add Contact</button>
        </div>
      </div>

      <input data-testid="contacts-search-input" className="input-tactile max-w-sm mb-4" placeholder="Search name or phone..." value={search} onChange={(e) => setSearch(e.target.value)} />

      <div className="card-tactile overflow-hidden">
        <table className="w-full text-sm" data-testid="contacts-table">
          <thead>
            <tr className="text-left border-b-2" style={{ background: "var(--cd-yellow)", borderColor: "var(--cd-navy)" }}>
              <th className="px-4 py-2.5 font-heading">Name</th>
              <th className="px-4 py-2.5 font-heading">WhatsApp Phone</th>
              <th className="px-4 py-2.5 font-heading">Tags</th>
              <th className="px-4 py-2.5 font-heading">Last Contacted</th>
              <th className="px-4 py-2.5 font-heading">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-b hover:bg-black/[0.03]" style={{ borderColor: "#E5DCB8" }} data-testid={`contact-row-${c.phone}`}>
                <td className="px-4 py-2.5 font-semibold">{c.name}</td>
                <td className="px-4 py-2.5 font-mono2">+{c.phone}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(c.tags || []).map((t) => <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded-full border" style={{ background: "var(--cd-green)", borderColor: "var(--cd-navy)" }}>{t}</span>)}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{c.last_contacted ? new Date(c.last_contacted).toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1">
                    <button data-testid={`edit-contact-${c.phone}`} className="btn-ghost2 py-1" onClick={() => { setForm({ name: c.name, phone: c.phone, email: c.email || "", tags: (c.tags || []).join(", ") }); setEditing(c.id); setOpen(true); }}><Pencil size={14} /></button>
                    <button data-testid={`delete-contact-${c.phone}`} className="btn-ghost2 py-1 text-red-600" onClick={() => remove(c.id)}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {contacts.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center font-hand text-lg text-amber-800">no contacts yet — add one or import a CSV</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-tactile card-yellow border-2" style={{ borderColor: "var(--cd-navy)" }}>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
          <label className="label-upper">Name</label>
          <input data-testid="contact-name-input" className="input-tactile" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <label className="label-upper mt-1">WhatsApp Phone (with country code)</label>
          <input data-testid="contact-phone-input" className="input-tactile" placeholder="919958709623" value={form.phone} disabled={!!editing} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <label className="label-upper mt-1">Email (optional)</label>
          <input data-testid="contact-email-input" className="input-tactile" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <label className="label-upper mt-1">Tags (comma separated)</label>
          <input data-testid="contact-tags-input" className="input-tactile" placeholder="VIP, Wholesale" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <button data-testid="contact-save-btn" className="btn-navy justify-center mt-2" disabled={!form.name || !form.phone} onClick={save}>Save Contact</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
